import { useCallback, useContext, useEffect, useRef } from "react"

import { graphql, useStaticQuery } from "gatsby"
import gsap from "gsap"
import styled from "styled-components"

import CarouselButton from "components/buttons/CarouselButton"
import { ScreenContext, ThinBannerContext } from "components/Providers"
import { ReactComponent as CloseSVG } from "images/button-icons/close.svg"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import { vwToPx } from "utils/functions"
import useMedia from "utils/useMedia"

export default function ThinBanner() {
  const { mobile } = useContext(ScreenContext)
  const offset = useMedia(90, vwToPx(5.422), vwToPx(7.324), 0)
  const wrapper = useRef<HTMLDivElement>(null)
  const { show, setShow } = useContext(ThinBannerContext)
  const burgerY = useMedia("32px", "2.222vw", "7.324vw", "30.667vw")

  const data: Queries.BannersQuery = useStaticQuery(graphql`
    query Banners {
      allContentfulTopBanner(
        sort: { startDate: ASC }
        filter: { id: { ne: "e1d5cedf-ed61-5f9b-ae4a-2605244cb63f" } }
      ) {
        nodes {
          bannerText
          button {
            buttonText
            url
          }
          endDate
          startDate
          id
        }
      }
    }
  `)

  const activeBanners = data.allContentfulTopBanner.nodes.filter(banner => {
    const now = Date.now()
    const bannerStart = new Date(banner.startDate ?? "Aug 31 1982").getTime()
    const bannerEnd = new Date(banner.endDate ?? "Aug 31 2055").getTime()

    return bannerStart <= now && bannerEnd >= now
  })

  const resizeBanner = useCallback(() => {
    if (wrapper.current) {
      const wrapperRef = wrapper.current
      if (show) {
        const { height } = wrapperRef.getBoundingClientRect()
        const setHeight = height - 2
        const offsetHeight = setHeight + offset
        const main = document.querySelector("main")
        const headerBurger = document.getElementById("headerBurger_open-close")

        gsap.set(main, { paddingTop: `${setHeight}px` })
        gsap.set("#nav", { y: `${offsetHeight}px` })
        if (headerBurger) {
          gsap.set(headerBurger, {
            y: burgerY,
          })
        }
      }
    }
  }, [show, offset, burgerY])

  useEffect(() => {
    window.addEventListener("resize", resizeBanner)
    return () => {
      window.removeEventListener("resize", resizeBanner)
    }
  }, [resizeBanner])

  const handleClose = () => {
    if (wrapper.current) {
      const tl = gsap.timeline()
      const { height } = wrapper.current.getBoundingClientRect()
      const main = document.querySelector("main")
      const headerBurger = document.getElementById("headerBurger_open-close")

      tl.to([wrapper.current], {
          y: `-${height}px`,
          duration: 0.3,
          onComplete: () => {
            gsap.set(wrapper.current, { display: "none" })

            setShow(false)
          },
        }, 0)
        .to(main, { paddingTop: 0, duration: 0.3 }, 0)
        .to("#nav", { y: 0, duration: 0.3 }, 0)

      if (headerBurger) {
        tl.to(headerBurger, {
            y: 0,
            duration: 0.3,
          }, 0)
      }
    }
  }

  useEffect(() => {
    if (show && wrapper.current) {
      const { height } = wrapper.current.getBoundingClientRect()
      const setHeight = height - 2
      const offsetHeight = setHeight + offset
      const main = document.querySelector("main")
      const headerBurger = document.getElementById("headerBurger_open-close")

      gsap.set(main, { paddingTop: `${setHeight}px` })
      gsap.set("#nav", { y: `${offsetHeight}px` })
      if (headerBurger) {
        gsap.set(headerBurger, {
          y: burgerY,
        })
      }

      if (!activeBanners[0]) {
        gsap.set(main, { paddingTop: 0 })
        gsap.set("#nav", { y: 0 })
        if (headerBurger) {
          gsap.set(headerBurger, {
            y: burgerY,
          })
        }

        setShow(false)
      }
    }
  }, [show, activeBanners, setShow, offset, burgerY])

  return show ? (
    <Wrapper
      ref={wrapper}
      onClick={handleClose}
      onKeyUp={handleClose}
      role="presentation"
    >
      <BannerText>{activeBanners[0]?.bannerText ?? ""}</BannerText>
      <Buttons>
        <CarouselButton
          variant="white"
          route={
            `${activeBanners[0]?.button?.url?.includes("http") ? "" : "/"}${
              activeBanners[0]?.button?.url ?? ""
            }` || ""
          }
        >
          {activeBanners[0]?.button?.buttonText ?? "Add text"}
        </CarouselButton>
        {mobile ? (
          <CarouselButton variant="clear" onClick={handleClose}>
            Close
          </CarouselButton>
        ) : (
          <Close>
            <CloseSVG />
          </Close>
        )}
      </Buttons>
    </Wrapper>
  ) : (
    <DisplayNone />
  )
}

const Wrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  background: ${colors.primHorz};
  z-index: 1000;
  top: 0;
  ${media.fullWidth} {
    padding: 15px 120px;
  }
  ${media.desktop} {
    padding: 0.904vw 7.229vw;
  }
  ${media.tablet} {
    padding: 2.441vw 8.789vw;
  }
  ${media.mobile} {
    overflow: hidden;
    flex-direction: column;
    padding: 4vw 5.333vw 5.333vw;
  }
`

const BannerText = styled.p`
  ${text.buttonR};
  text-align: center;

  color: ${colors.white};
  ${media.fullWidth} {
    max-width: 960px;
  }
  ${media.desktop} {
    max-width: 57.831vw;
  }
  ${media.tablet} {
    max-width: 57.715vw;
  }
  ${media.mobile} {
    max-width: 100%;
  }
`

const Buttons = styled.div`
  display: flex;

  ${media.fullWidth} {
    gap: 100px;
  }
  ${media.desktop} {
    gap: 6.024vw;
  }
  ${media.tablet} {
    gap: 3.906vw;
  }
  ${media.mobile} {
    gap: 2.667vw;
    flex-direction: row-reverse;
    flex-wrap: wrap;
    justify-content: center;
    margin-top: 2.667vw;
  }
`

const Close = styled.button`
  svg {
    ${media.fullWidth} {
      width: 25px;
      height: 25px;
    }
    ${media.desktop} {
      width: 1.506vw;
      height: 1.506vw;
    }
    ${media.tablet} {
      width: 2.441vw;
      height: 2.441vw;
    }
  }
`

const DisplayNone = styled.div`
  display: none;
  height: 0;
  position: absolute;
  top: 0;
  left: 0;
`
